import passwordValidator from 'password-validator';
import zxcvbn from 'zxcvbn';

/**
 * Regular expression matching allowed special characters for passwords.
 * Excludes + and = characters as per password policy requirements.
 */
const allowedSpecialChars = /[!@#$%^&*(){}[\]<>?/\\|,.~`"':;_-]/;

/**
 * Main password validation schema enforcing all password requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one digit
 * - At least one symbol (from allowedSpecialChars)
 * - No spaces
 */
const schema = new passwordValidator();

schema
  .is()
  .min(8)
  .has()
  .uppercase()
  .has()
  .lowercase()
  .has()
  .digits()
  .has()
  .symbols()
  .has(allowedSpecialChars)
  .has()
  .not()
  .spaces();

/**
 * Validates that a password meets all structural requirements.
 * Checks for minimum length, character types, and format restrictions.
 *
 * @param input - The password string to validate
 * @returns True if password meets all requirements, false otherwise
 */
export const isValidPassword = (input: string): boolean => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const result = schema.validate(input);
  return typeof result === 'boolean' ? result : false;
};

/**
 * Schema for validating minimum password length (8 characters, no spaces).
 * Used for granular feedback on individual password requirements.
 */
const lengthSchema = new passwordValidator();
lengthSchema.is().min(8).has().not().spaces();

/**
 * Validates that a password is at least 8 characters long and contains no spaces.
 * Used for providing specific feedback on password length requirement.
 *
 * @param input - The password string to validate
 * @returns True if password is at least 8 characters with no spaces, false otherwise
 */
export const isAtLeastEightChars = (input: string): boolean => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const result = lengthSchema.validate(input);
  return typeof result === 'boolean' ? result : false;
};

/**
 * Schema for validating presence of lowercase letters (no spaces allowed).
 * Used for granular feedback on individual password requirements.
 */
const lowerCaseLetterSchema = new passwordValidator();
lowerCaseLetterSchema.has().lowercase().has().not().spaces();

/**
 * Validates that a password contains at least one lowercase letter and no spaces.
 * Used for providing specific feedback on lowercase letter requirement.
 *
 * @param input - The password string to validate
 * @returns True if password contains lowercase letter(s) and no spaces, false otherwise
 */
export const hasLowerCaseLetter = (input: string): boolean => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const result = lowerCaseLetterSchema.validate(input);
  return typeof result === 'boolean' ? result : false;
};

/**
 * Schema for validating presence of uppercase letters (no spaces allowed).
 * Used for granular feedback on individual password requirements.
 */
const upperCaseLetterSchema = new passwordValidator();
upperCaseLetterSchema.has().uppercase().has().not().spaces();

/**
 * Validates that a password contains at least one uppercase letter and no spaces.
 * Used for providing specific feedback on uppercase letter requirement.
 *
 * @param input - The password string to validate
 * @returns True if password contains uppercase letter(s) and no spaces, false otherwise
 */
export const hasUpperCaseLetter = (input: string): boolean => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const result = upperCaseLetterSchema.validate(input);
  return typeof result === 'boolean' ? result : false;
};

/**
 * Schema for validating presence of special characters from the allowed set (no spaces allowed).
 * Used for granular feedback on individual password requirements.
 */
const specialCharSchema = new passwordValidator();
specialCharSchema.has().symbols().has(allowedSpecialChars).has().not().spaces();

/**
 * Validates that a password contains at least one special character from the allowed set
 * and no spaces. Allowed special characters exclude + and = as per password policy.
 *
 * @param input - The password string to validate
 * @returns True if password contains allowed special character(s) and no spaces, false otherwise
 */
export const hasSpecialCharacter = (input: string): boolean => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const result = specialCharSchema.validate(input);
  return typeof result === 'boolean' ? result : false;
};

/**
 * Schema for validating presence of digits (no spaces allowed).
 * Used for granular feedback on individual password requirements.
 */
const digitsSchema = new passwordValidator();
digitsSchema.has().digits().has().not().spaces();

/**
 * Validates that a password contains at least one digit and no spaces.
 * Used for providing specific feedback on digit requirement.
 *
 * @param input - The password string to validate
 * @returns True if password contains digit(s) and no spaces, false otherwise
 */
export const hasDigit = (input: string): boolean => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const result = digitsSchema.validate(input);
  return typeof result === 'boolean' ? result : false;
};

/**
 * Validates that a password is not too easily guessable using zxcvbn strength scoring.
 * zxcvbn scores passwords on a scale of 0-4:
 * - 0-1: Very weak (too guessable)
 * - 2: Moderately strong (acceptable)
 * - 3-4: Strong (excellent)
 *
 * This function requires a score of at least 2 to be considered "not too guessable".
 *
 * @param input - The password string to validate
 * @returns True if password has zxcvbn score >= 2, false otherwise (or on error)
 */
export const notTooGuessable = (input: string): boolean => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  try {
    const result = zxcvbn(input);
    return result.score >= 2;
  } catch (err) {
    // If zxcvbn fails for any reason, fail securely by returning false
    if (process.env.NODE_ENV === 'development') {
      console.warn('zxcvbn validation error:', err);
    }
    return false;
  }
};
